import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text, Surface, IconButton, List, Checkbox, useTheme, Button, Portal, Snackbar, Dialog, Paragraph, TextInput } from 'react-native-paper';
import { useRoute, RouteProp, useNavigation, NavigationProp } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/Feather';
import { formatCurrency, formatDate } from '../utils/format';
import { Cliente } from '../types/Cliente';
import { supabase } from '../services/supabase';
import { whatsappService } from '../services/whatsappService';

type RootStackParamList = {
  DrawerHome: undefined;
  ListaTitulos: { cliente: Cliente };
  AgendamentoCobranca: { titulo: Titulo };
  AgendamentoMultiplo: { titulos: Titulo[], cliente: Cliente };
  DetalheCliente: { clienteId: number, cliente: { CODCLI: string } };
};

type ListaTitulosRouteProp = RouteProp<RootStackParamList, 'ListaTitulos'>;
type AppNavigationProp = NavigationProp<RootStackParamList>;

interface Titulo {
  D_DTPAG: string;
  F_VALOR: string;
  S_PREST: string;
  D_DTVENC: string;
  I_CODCLI: string;
  I_DUPLIC: string;
  I_CODUSUR: string;
  S_LINHADIG: string;
  D_DTEMISSAO: string;
  I_COBRANCAS: string;
  I_CODFILIAL: string;
  D_DTVENCORIG: string;
  S_CODBANCOCM: string;
  S_DUPLICPREST: string;
  S_NOSSONUMBCO: string;
  I_NUMTRANSVENDA: string;
  S_LINHADIGFORMATADA: string;
}

interface UserData {
  empresa: number;
  codusur: string;
}

export function ListaTitulos() {
  const route = useRoute<ListaTitulosRouteProp>();
  const navigation = useNavigation<AppNavigationProp>();
  const { cliente } = route.params;
  const theme = useTheme();
  const [titulos, setTitulos] = useState<Titulo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);

  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const [selectedTitulos, setSelectedTitulos] = useState<Titulo[]>([]);
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);
  const [tituloSelecionado, setTituloSelecionado] = useState<Titulo | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [telefoneEnvio, setTelefoneEnvio] = useState('5582988715493'); // Padrão que pode ser alterado
  
  // Estado para o Snackbar
  const [snackbar, setSnackbar] = useState({
    visible: false,
    mensagem: '',
    tipo: 'info' as 'info' | 'success' | 'error'
  });

  const handleToggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Abre o diálogo de confirmação para envio de WhatsApp
  const handleWhatsApp = (titulo: Titulo) => {
    setTituloSelecionado(titulo);
    
    // Verifica se o cliente tem o telefone já formatado
    console.log('Verificando telefone do cliente para WhatsApp individual...');
    console.log('S_TELCOBFORMATADO:', cliente.S_TELCOBFORMATADO);
    
    // Usa o telefone já formatado se disponível
    if (cliente.S_TELCOBFORMATADO && cliente.S_TELCOBFORMATADO.length > 5) {
      console.log('Usando telefone formatado do cliente:', cliente.S_TELCOBFORMATADO);
      setTelefoneEnvio(cliente.S_TELCOBFORMATADO);
    } else {
      // Caso não tenha o telefone formatado, tenta outros campos
      console.log('Telefone formatado não encontrado, verificando S_TELCOB...');
      console.log('S_TELCOB:', cliente.S_TELCOB);
      
      // Usa o campo S_TELCOB como backup
      let telefoneCliente = cliente.S_TELCOB || '';
      
      // Remove caracteres não numéricos do telefone
      telefoneCliente = telefoneCliente.replace(/\D/g, '');
      
      // Se o telefone não começar com 55 (Brasil), adiciona
      if (telefoneCliente && !telefoneCliente.startsWith('55')) {
        telefoneCliente = `55${telefoneCliente}`;
      }
      
      // Define um telefone padrão se não encontrar nenhum válido
      const telefoneEnviar = (telefoneCliente && telefoneCliente.length > 5) ? telefoneCliente : '55829';
      console.log('Telefone definido para envio:', telefoneEnviar);
      setTelefoneEnvio(telefoneEnviar);
    }
    
    // Apenas abre o diálogo, a verificação será feita no momento do envio
    // para considerar qualquer alteração feita pelo usuário
    setDialogVisible(true);
  };
  
  // Função para enviar mensagem de WhatsApp para um título específico
  const enviarMensagemWhatsApp = async () => {
    if (!tituloSelecionado) return;
    
    const titulo = tituloSelecionado;
    try {
      // Verificar se o código da empresa está disponível
      if (!userData || !userData.empresa) {
        throw new Error('Código da empresa não disponível');
      }
      
      console.log('Código da empresa disponível:', userData.empresa);
      
      // Verificar se o número é um WhatsApp válido
      // Aqui estamos usando o telefoneEnvio que pode ter sido editado pelo usuário
      console.log('Verificando se o número é um WhatsApp válido:', telefoneEnvio);
      const numeroValido = await whatsappService.verificarNumeroWhatsApp(telefoneEnvio, userData.empresa);
      
      if (!numeroValido) {
        setSnackbar({
          visible: true,
          mensagem: 'O número informado não é um WhatsApp válido. Verifique e tente novamente.',
          tipo: 'error'
        });
        setDialogVisible(false); // Fecha o diálogo em caso de erro
        return;
      }
      
      // Só inicia o envio se o número for válido
      setEnviandoMensagem(true);
      setDialogVisible(false); // Fecha o diálogo para iniciar o envio
      
      console.log('Número de WhatsApp válido, prosseguindo com o envio...');
      
      // Busca o modelo de mensagem no banco de dados com n8n=false
      const { data: modeloData, error: modeloError } = await supabase
        .from('modelomensagem')
        .select('*')
        .eq('codempresa', userData.empresa)
        .eq('n8n', false)
        .limit(1);
        
      if (modeloError) {
        console.error('Erro ao buscar modelo de mensagem:', modeloError);
        throw new Error('Erro ao buscar modelo de mensagem');
      }
      
      if (!modeloData || modeloData.length === 0) {
        throw new Error('Modelo de mensagem não encontrado no banco de dados');
      }
      
      const modeloMensagem = modeloData[0].modelo;
      
      if (!modeloMensagem) {
        throw new Error('Modelo de mensagem inválido');
      }
      
      // Prepara os valores formatados
      const valorFormatado = formatCurrency(titulo.F_VALOR);
      const vencimentoFormatado = formatDate(titulo.D_DTVENC);
      const emissaoFormatada = formatDate(titulo.D_DTEMISSAO);
      
      // Substitui as variáveis no modelo
      let mensagem = modeloMensagem;
      
      // Buscar dados da empresa
      const { data: empresaData, error: empresaError } = await supabase
        .from('empresa')
        .select('*')
        .eq('id', userData.empresa)
        .single();
        
      if (empresaError) {
        console.error('Erro ao buscar dados da empresa:', empresaError);
        throw new Error('Erro ao buscar dados da empresa');
      }
      
      // Mapeamento de variáveis do modelo para os dados do título
      const substituicoes: Record<string, string> = {
        '[cliente]': cliente.S_CLIENTE || '',
        '[codcli]': String(cliente.I_CODCLI || ''),
        '[cgc]': cliente.S_CGCENT || '',
        '[valor]': valorFormatado,
        '[valortitulo]': valorFormatado,
        '[duplicata]': titulo.S_DUPLICPREST || '',
        '[duplic]': titulo.S_DUPLICPREST || '',
        '[vencimento]': vencimentoFormatado,
        '[dtvenc]': vencimentoFormatado,
        '[emissao]': emissaoFormatada,
        '[banco]': titulo.S_CODBANCOCM || '',
        '[nossonumero]': titulo.S_NOSSONUMBCO || '',
        '[linhadigitavel]': titulo.S_LINHADIGFORMATADA || '',
        '[linhadig]': titulo.S_LINHADIGFORMATADA || '',
        '[empresanome]': empresaData?.nome || '',
        '[empresatel]': empresaData?.telefone || '',
        '[empresacnpj]': empresaData?.cnpj || ''
      };
      
      // Aplica todas as substituições de forma limpa
      Object.entries(substituicoes).forEach(([chave, valor]) => {
        // Escape caracteres especiais em regex
        const chaveEscapada = chave.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        mensagem = mensagem.replace(new RegExp(chaveEscapada, 'g'), valor);
      });
      
      // Usar o telefone definido na confirmação
      const telefone = telefoneEnvio;
      
      // Envia a mensagem e registra a cobrança
      await whatsappService.enviarTexto({ 
        telefone, 
        mensagem,
        // Passa o codempresa para o serviço
        codempresa: userData.empresa,
        options: {
          delay: 1200
        },
        // Registra a cobrança na API
        registrarCobranca: true,
        dadosCobranca: {
          codfunc: userData.codusur,
          codcli: String(cliente.I_CODCLI || ''),
          titulos: [titulo] // Passa apenas o título selecionado
        }
      });
      
      setSnackbar({ visible: true, mensagem: 'Mensagem enviada com sucesso!', tipo: 'success' });
      setDialogVisible(false);
      
      // Atualiza os dados após o envio da cobrança
      console.log('Atualizando dados após cobrança individual...');
      await fetchTitulos(); // Recarrega os títulos para mostrar os dados atualizados
    } catch (error: any) {
      console.error('Erro ao enviar mensagem:', error);
      setSnackbar({ visible: true, mensagem: `Erro ao enviar mensagem: ${error.message}`, tipo: 'error' });
    } finally {
      setEnviandoMensagem(false);
      setTituloSelecionado(null);
    }
  };

  const handleCalendario = (titulo: Titulo) => {
    navigation.navigate('AgendamentoCobranca', { titulo });
  };

  const toggleTituloSelection = (titulo: Titulo) => {
    setSelectedTitulos(prev => {
      const isSelected = prev.some(t => t.I_DUPLIC === titulo.I_DUPLIC);
      if (isSelected) {
        return prev.filter(t => t.I_DUPLIC !== titulo.I_DUPLIC);
      } else {
        return [...prev, titulo];
      }
    });
  };

  const handleAgendamentoMultiplo = () => {
    if (selectedTitulos.length > 0) {
      navigation.navigate('AgendamentoMultiplo', { titulos: selectedTitulos, cliente });
    }
  };

  const fetchTitulos = async () => {
    try {
      setLoading(true);
      setError(null);

      // Primeiro busca o usuário logado
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Busca o codempresa do usuário
      const { data: userDataResult, error: userError } = await supabase
        .from('usuarios')
        .select('empresa, codusur')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userDataResult?.empresa) throw new Error('Empresa não encontrada');
      
      // Salva os dados do usuário
      setUserData({
        empresa: userDataResult.empresa,
        codusur: userDataResult.codusur
      });

      // Busca os títulos
      const { data, error } = await supabase
        .from('listasconsultaapi')
        .select('data')
        .eq('rotaapi', 'FINANCEIROListaTituloDeb')
        .eq('codempresa', userDataResult.empresa)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data[0]?.data) {
        const titulosArray = Array.isArray(data[0].data) ? data[0].data : [];
        // Filtra apenas os títulos do cliente selecionado
        const titulosCliente = titulosArray.filter(titulo => 
          titulo.I_CODCLI === cliente.I_CODCLI
        );
        setTitulos(titulosCliente);
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Erro ao buscar títulos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalhesCliente = () => {
    navigation.navigate('DetalheCliente', { 
      clienteId: cliente.id || 0,
      cliente: {
        CODCLI: String(cliente.I_CODCLI || '')  
      }
    });
  };

  React.useEffect(() => {
    fetchTitulos();
  }, [cliente]);

  return (
    <ScrollView style={styles.container}>
      <Surface style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.headerInfo}>
            <Text variant="titleLarge" style={styles.clientName}>{cliente.S_CLIENTE}</Text>
            <Text variant="bodyMedium" style={styles.infoText}>CNPJ: {cliente.S_CGCENT || ''}</Text>
            <Text variant="bodyMedium" style={styles.infoText}>Valor Débito: {formatCurrency(cliente.F_VALORDEB || '0')}</Text>
            <View style={styles.quantityRow}>
              <Text variant="bodyMedium" style={styles.infoText}>Qtde. Títulos: {cliente.I_QTDETITULOS}</Text>
              <Text variant="bodyMedium" style={styles.infoText}>Qtde. Cobranças: {cliente.I_COBRANCAS}</Text>
            </View>
          </View>
          <View style={styles.clienteHeader}>
            <Text style={styles.clienteNome}>{cliente.S_CLIENTE}</Text>
            <View style={styles.clienteInfoGrid}>
              <View style={styles.clienteInfoItem}>
                <IconButton icon="card-account-details" size={20} iconColor="#666" style={styles.clienteInfoIcon} />
                <View>
                  <Text style={styles.clienteInfoLabel}>CNPJ</Text>
                  <Text style={styles.clienteInfoValue}>{cliente.S_CGCENT}</Text>
                </View>
              </View>
              <View style={styles.clienteInfoItem}>
                <IconButton icon="cash-multiple" size={20} iconColor="#F44336" style={styles.clienteInfoIcon} />
                <View>
                  <Text style={styles.clienteInfoLabel}>Valor Débito</Text>
                  <Text style={[styles.clienteInfoValue, styles.clienteInfoValueMoney]}>{formatCurrency(cliente.F_VALORDEB || '0')}</Text>
                </View>
              </View>
              <View style={styles.clienteInfoItem}>
                <IconButton icon="file-document-multiple" size={20} iconColor="#FF9800" style={styles.clienteInfoIcon} />
                <View>
                  <Text style={styles.clienteInfoLabel}>Títulos</Text>
                  <Text style={styles.clienteInfoValue}>{cliente.I_QTDETITULOS}</Text>
                </View>
              </View>
              <View style={styles.clienteInfoItem}>
                <IconButton icon="message-alert" size={20} iconColor="#2196F3" style={styles.clienteInfoIcon} />
                <View>
                  <Text style={styles.clienteInfoLabel}>Cobranças</Text>
                  <Text style={styles.clienteInfoValue}>{cliente.I_COBRANCAS}</Text>
                </View>
              </View>
            </View>
            <Button 
              mode="contained" 
              onPress={handleVerDetalhesCliente}
              style={styles.detalhesClienteButton}
              icon="eye"
            >
              Ver Detalhes
            </Button>
          </View>
          {selectedTitulos.length > 0 && (
            <View style={styles.headerActions}>
              <Text style={styles.selectedCount}>{selectedTitulos.length} títulos selecionados</Text>
              <Button
                mode="contained"
                onPress={handleAgendamentoMultiplo}
                style={styles.agendarButton}
              >
                Agendar Selecionados
              </Button>
            </View>
          )}
        </View>
      </Surface>
      
      {/* Snackbar para feedback */}
      <Portal>
        <Snackbar
          visible={snackbar.visible}
          onDismiss={() => setSnackbar(prev => ({ ...prev, visible: false }))}
          duration={3000}
          style={{
            backgroundColor: 
              snackbar.tipo === 'success' ? '#4CAF50' : 
              snackbar.tipo === 'error' ? '#F44336' :
              '#2196F3'
          }}
        >
          {snackbar.mensagem}
        </Snackbar>
        
        {/* Diálogo de confirmação para envio de WhatsApp */}
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)}>
          <Dialog.Title>Confirmar envio de mensagem</Dialog.Title>
          <Dialog.Content>
            <Paragraph>Deseja enviar mensagem de cobrança para o título {tituloSelecionado?.S_DUPLICPREST}?</Paragraph>
            <Paragraph>Valor: {tituloSelecionado ? formatCurrency(tituloSelecionado.F_VALOR) : ''}</Paragraph>
            <Paragraph>Vencimento: {tituloSelecionado ? formatDate(tituloSelecionado.D_DTVENC) : ''}</Paragraph>
            <Paragraph>Cliente: {cliente.S_CLIENTE}</Paragraph>
            <View style={styles.telefoneInput}>
              <Text>Telefone para envio:</Text>
              <TextInput
                value={telefoneEnvio}
                onChangeText={setTelefoneEnvio}
                style={styles.input}
                keyboardType="number-pad"
                mode="outlined"
              />
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>Cancelar</Button>
            <Button
              mode="contained"
              onPress={enviarMensagemWhatsApp}
              loading={enviandoMensagem}
              disabled={enviandoMensagem}
            >
              Enviar
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {titulos.map((titulo) => {
        const isSelected = selectedTitulos.some(t => t.I_DUPLIC === titulo.I_DUPLIC);
        
        return (
          <Surface key={`${titulo.I_DUPLIC}-${titulo.S_PREST}`} style={[styles.card, isSelected && styles.cardSelected]} elevation={isSelected ? 3 : 1}>
            <View style={styles.cardContent}>
              <View style={styles.cardMain}>
                <Checkbox.Android
                  status={isSelected ? 'checked' : 'unchecked'}
                  onPress={() => toggleTituloSelection(titulo)}
                  style={styles.checkbox}
                />
                <View style={styles.cardInfo}>
                  <View style={styles.cardRow}>
                    <Text style={styles.duplicata}>{titulo.S_DUPLICPREST}</Text>
                    <Text style={styles.valor}>{formatCurrency(titulo.F_VALOR)}</Text>
                  </View>
                  <Text style={styles.vencimento}>Vencimento: {formatDate(titulo.D_DTVENC)}</Text>
                </View>
                <View style={styles.cardActionsCompact}>
                  <IconButton
                    icon="whatsapp"
                    iconColor="#25D366"
                    size={22}
                    onPress={() => handleWhatsApp(titulo)}
                    style={styles.actionButtonCompact}
                  />
                  <IconButton
                    icon="calendar"
                    iconColor="#2196F3"
                    size={22}
                    onPress={() => handleCalendario(titulo)}
                    style={styles.actionButtonCompact}
                  />
                  <IconButton
                    icon={expandedItems[`${titulo.I_DUPLIC}-${titulo.S_PREST}`] ? 'chevron-up' : 'chevron-down'}
                    iconColor="#666"
                    size={22}
                    onPress={() => handleToggleExpand(`${titulo.I_DUPLIC}-${titulo.S_PREST}`)}
                    style={styles.actionButtonCompact}
                  />
                </View>
              </View>
              
              {expandedItems[`${titulo.I_DUPLIC}-${titulo.S_PREST}`] && (
                <View style={styles.boletoDetails}>
                  <View style={styles.boletoDetailRow}>
                    <IconButton icon="bank" size={16} style={styles.boletoIcon} />
                    <Text style={styles.boletoDetailText}>Banco: {titulo.S_CODBANCOCM}</Text>
                  </View>
                  <View style={styles.boletoDetailRow}>
                    <IconButton icon="numeric" size={16} style={styles.boletoIcon} />
                    <Text style={styles.boletoDetailText}>Nosso Número: {titulo.S_NOSSONUMBCO}</Text>
                  </View>
                  <View style={styles.boletoDetailRow}>
                    <IconButton icon="barcode" size={16} style={styles.boletoIcon} />
                    <Text style={styles.boletoDetailText}>Linha Digitável: {titulo.S_LINHADIGFORMATADA}</Text>
                  </View>
                </View>
              )}
            </View>
          </Surface>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 10,
    backgroundColor: '#f5f5f5',
  },
  header: {
    marginBottom: 10,
    borderRadius: 8,
    elevation: 2,
  },
  headerContent: {
    padding: 10,
  },
  telefoneInput: {
    marginTop: 10,
    marginBottom: 10,
  },
  input: {
    marginTop: 5,
    backgroundColor: '#f9f9f9',
  },
  headerInfo: {
    flex: 1,
  },
  clientName: {
    marginBottom: 12,
    fontSize: 20,
    fontWeight: 'bold',
  },
  infoText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  quantityRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 16,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 8,
    backgroundColor: '#f0f0f0',
    padding: 12,
    borderRadius: 8,
  },
  selectedCount: {
    fontSize: 14,
    color: '#666',
    marginRight: 16,
  },
  agendarButton: {
    marginLeft: 0,
  },
  clienteInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginVertical: 16,
  },
  clienteInfoItem: {
    flex: 1,
    minWidth: '45%',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
  },
  clienteInfoIcon: {
    margin: 0,
    marginRight: 4,
  },
  clienteInfoLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 2,
  },
  clienteInfoValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
  },
  clienteInfoValueMoney: {
    color: '#F44336',
  },
  card: {
    marginBottom: 12,
    borderRadius: 12,
    elevation: 1,
    backgroundColor: '#fff',
  },
  cardSelected: {
    borderLeftWidth: 4,
    borderLeftColor: '#2196F3',
    backgroundColor: '#f0f7ff',
  },
  cardContent: {
    padding: 12,
  },
  cardMain: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkbox: {
    margin: 0,
  },
  cardInfo: {
    flex: 1,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  vencimento: {
    fontSize: 12,
    color: '#666',
  },
  cardActionsCompact: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButtonCompact: {
    margin: 0,
  },
  boletoDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  boletoIcon: {
    margin: 0,
    marginRight: -4,
  },
  boletoDetailText: {
    fontSize: 13,
    color: '#666',
  },
  tituloHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  duplicata: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  vencimentoRow: {
    marginBottom: 8,
  },
  valor: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2196f3',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  boletoDetails: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  checkboxContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  selectText: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  clienteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  clienteNome: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  clienteCNPJ: {
    fontSize: 14,
    color: '#666',
  },
  detalhesClienteButton: {
    marginLeft: 16,
  },
});
